import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import {
  Check, Loader2, AlertCircle, User, Calendar, CreditCard,
  FileSignature, Plus, Trash2, ChevronRight, Trophy, DollarSign, ChevronsUpDown
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Card, CardContent } from "../components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";
import { API } from "../config/api";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Normalize FastAPI error responses (which may be a string OR a list of
// validation error objects from Pydantic) into a plain string for toast.
function errToMessage(err, fallback = "Something went wrong") {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const field = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== "body").join(".") : "";
        return field ? `${field}: ${d?.msg || "invalid"}` : (d?.msg || "invalid");
      })
      .join("; ");
  }
  return fallback;
}

// Pole option sets — same lengths used in the admin Poles tab dropdown.
const POLE_LENGTHS = ["9'0", "10'0", "10'6", "11'0", "11'6", "12'1", "12'7", "13'1", "13'7", "14'1", "14'7", "15'1", "15'7", "16'1", "16'5", "16'9"];
// Common ESSX pole weights in 5lb increments (rest can be typed in the search).
const POLE_WEIGHTS = ["80", "85", "90", "95", "100", "105", "110", "115", "120", "125", "130", "135", "140", "145", "150", "155", "160", "165", "170", "175", "180", "185", "190", "195", "200", "210", "220"];

// Searchable single-select. Allows custom values that aren't in the list.
function SearchableSelect({ value, onChange, options, placeholder = "Select…", testId, allowCustom = true }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const hasCustomMatch = allowCustom && search && !options.includes(search);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid={testId}
          type="button"
          variant="outline"
          role="combobox"
          className="w-full justify-between font-normal"
        >
          <span className={value ? "" : "text-slate-400"}>{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={true}>
          <CommandInput
            placeholder="Search or type…"
            value={search}
            onValueChange={setSearch}
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <CommandList>
            <CommandEmpty>
              {allowCustom && search ? (
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100"
                  onClick={() => { onChange(search); setOpen(false); setSearch(""); }}
                >
                  Use <strong>&quot;{search}&quot;</strong>
                </button>
              ) : <span className="text-sm text-slate-400 px-3 py-2 block">No matches</span>}
            </CommandEmpty>
            <CommandGroup>
              {hasCustomMatch && (
                <CommandItem
                  key={`custom-${search}`}
                  value={`__custom__${search}`}
                  onSelect={() => { onChange(search); setOpen(false); setSearch(""); }}
                >
                  Use <span className="ml-1 font-semibold">&quot;{search}&quot;</span>
                </CommandItem>
              )}
              {options.map((opt) => (
                <CommandItem
                  key={opt}
                  value={opt}
                  onSelect={() => { onChange(opt); setOpen(false); setSearch(""); }}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === opt ? "opacity-100" : "opacity-0"}`} />
                  {opt}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Meet rental pricing (per 4-day rental period). Only depends on # of poles in the request.
// 1 pole = $40 · 2–3 poles = $30 each · 4+ poles = $25 each.
function computeMeetEstimate(poleCount) {
  if (poleCount <= 0) return { total: 0, perPole: 0, tier: '' };
  if (poleCount === 1) return { total: 40, perPole: 40, tier: '1 pole' };
  if (poleCount <= 3) return { total: poleCount * 30, perPole: 30, tier: '2–3 poles' };
  return { total: poleCount * 25, perPole: 25, tier: '4+ poles' };
}

const AGREEMENT_TEXT = `Circus Pole Vault LLC — Meet Rental Agreement & Assumption of Risk

This Meet Rental Agreement ("Agreement") is entered into between Circus Pole Vault LLC ("Lessor") and the undersigned renter ("Renter"), which may be an individual, school, club, or organization.

1. Rental Term. Meet rentals are short-term rentals for a single track meet or event. Pickup and return dates are specified on this form and on any invoice issued.

2. Responsibility for Use & Users. The Renter assumes full responsibility for the rented equipment and for ALL persons who use, access, or handle the equipment, including but not limited to athletes, students, coaches, and third parties.

3. Assumption of Risk & Release of Liability. Pole vaulting and related activities involve inherent risks of serious injury or death. The Renter knowingly and voluntarily assumes all risks associated with the use of any rented equipment. Circus Pole Vault LLC shall NOT be liable for any injury, death, property damage, or loss arising from the use, misuse, or condition of the equipment, to the fullest extent permitted by law.

4. Equipment Responsibility. The Renter is financially responsible for any lost, stolen, destroyed, or damaged poles, regardless of cause.

5. Insurance is NOT offered on meet rentals. The Renter agrees to return ALL poles UNDAMAGED. Any damage, loss, or theft will be billed in full to the card on file.

6. Pole Exchanges. Pole exchanges during a meet are subject to availability and incur a $35 exchange/restocking fee per pole.

7. Scope of Agreement. This Agreement applies to the equipment listed on this form and to any future meet rentals from Circus Pole Vault LLC.

8. Age, Authority & Minors. The undersigned certifies they are at least eighteen (18) years old and legally authorized to sign. For any user under eighteen (18), a parent or legal guardian must execute this Agreement.

9. Governing Law. This Agreement shall be governed by the laws of the state in which Circus Pole Vault LLC operates.

*Card on file is NOT charged at submission. Card will be billed when poles are picked up.`;


// ---------- Signature Pad ----------
function SignaturePad({ onSignatureChange }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const endDraw = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    setHasSignature(true);
    onSignatureChange(canvasRef.current.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onSignatureChange('');
  };

  return (
    <div>
      <div className="border-2 border-dashed border-slate-300 rounded-lg bg-white relative">
        <canvas
          ref={canvasRef}
          width={500}
          height={150}
          className="w-full cursor-crosshair touch-none"
          style={{ height: '150px' }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
        />
        {!hasSignature && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-slate-400 text-sm">
            Sign here
          </div>
        )}
      </div>
      {hasSignature && (
        <Button variant="ghost" size="sm" onClick={clear} className="mt-1 text-xs text-slate-500" data-testid="clear-signature-btn">
          Clear signature
        </Button>
      )}
    </div>
  );
}


// ---------- Card form ----------
function CardForm({ customerId, clientSecret, onPaymentMethodReady }) {
  const stripe = useStripe();
  const elements = useElements();
  const [cardComplete, setCardComplete] = useState(false);

  useEffect(() => {
    if (stripe && elements && clientSecret && cardComplete) {
      onPaymentMethodReady(async () => {
        const cardElement = elements.getElement(CardElement);
        const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
          payment_method: { card: cardElement }
        });
        if (error) throw new Error(error.message);
        return setupIntent.payment_method;
      });
    } else {
      onPaymentMethodReady(null);
    }
  }, [stripe, elements, clientSecret, cardComplete, onPaymentMethodReady, customerId]);

  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-white">
      <CardElement
        options={{
          style: {
            base: { fontSize: '16px', color: '#1e293b', '::placeholder': { color: '#94a3b8' } },
            invalid: { color: '#ef4444' }
          }
        }}
        onChange={(e) => setCardComplete(e.complete)}
      />
    </div>
  );
}


// ---------- Main ----------
function MeetRentalFormContent() {
  const [step, setStep] = useState('form'); // form | complete
  const today = new Date().toISOString().split('T')[0];

  const [form, setForm] = useState({
    athlete_name: '',
    athlete_is_minor: false,
    renter_name: '',
    phone: '',
    email: '',
    meet_name: '',
    pickup_date: '',
    return_date: '',
    authorized_rep: '',
    signature_date: today,
    agree_terms: false,
    notes: '',
  });
  const [poles, setPoles] = useState([{ length: '', weight: '' }]);
  const [signature, setSignature] = useState('');
  const [honeypot, setHoneypot] = useState('');

  // Stripe state — SetupIntent prepared once renter info is filled
  const [stripeReady, setStripeReady] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeData, setStripeData] = useState({ clientSecret: '', customerId: '' });
  const [confirmPaymentFn, setConfirmPaymentFn] = useState(null);

  const [submitting, setSubmitting] = useState(false);

  // Poles helpers
  const addPole = () => setPoles(p => [...p, { length: '', weight: '' }]);
  const removePole = (i) => setPoles(p => p.length > 1 ? p.filter((_, idx) => idx !== i) : p);
  const setPole = (i, field, val) => setPoles(p => p.map((row, idx) => idx === i ? { ...row, [field]: val } : row));

  // Renter info complete? (needed for SetupIntent). Email must look complete (has @ and a TLD)
  // so we don't fire a SetupIntent on every keystroke while the user is still typing the address.
  const renterInfoReady = useMemo(() => {
    const emailLooksComplete = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email || '');
    return Boolean(
      form.athlete_name && form.phone && emailLooksComplete &&
      (!form.athlete_is_minor || form.renter_name)
    );
  }, [form.athlete_name, form.email, form.phone, form.athlete_is_minor, form.renter_name]);

  // Once renter info looks complete, prep the SetupIntent so card field can be confirmed at submit.
  // Debounced 600ms so we don't fire mid-typing.
  useEffect(() => {
    if (!renterInfoReady) return;
    let cancelled = false;
    const fullName = form.athlete_is_minor && form.renter_name ? form.renter_name : form.athlete_name;
    const init = async () => {
      setStripeReady(false);
      setStripeLoading(true);
      try {
        const res = await axios.post(`${API}/meet-rentals/setup-intent`, {
          full_name: fullName,
          email: form.email,
          phone: form.phone,
        });
        if (cancelled) return;
        setStripeData({ clientSecret: res.data.client_secret, customerId: res.data.customer_id });
        setStripeReady(true);
      } catch (err) {
        if (!cancelled) toast.error(errToMessage(err, "Failed to initialize payment"));
      } finally {
        if (!cancelled) setStripeLoading(false);
      }
    };
    const timer = setTimeout(init, 600);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [renterInfoReady, form.email, form.phone, form.athlete_name, form.renter_name, form.athlete_is_minor]);

  const polesValid = poles.length > 0 && poles.every(p => p.length && p.weight);
  const datesValid = form.pickup_date && form.return_date && form.pickup_date <= form.return_date;

  // Live price estimate based on pole count
  const validPoleCount = poles.filter(p => p.length && p.weight).length;
  const estimate = useMemo(() => computeMeetEstimate(validPoleCount), [validPoleCount]);

  // When athlete is a minor, the signer (authorized_rep) must match the renter (coach/parent/guardian)
  const signerMatchesRenter = (
    !form.athlete_is_minor ||
    (form.authorized_rep || '').trim().toLowerCase() === (form.renter_name || '').trim().toLowerCase()
  );

  const canSubmit = (
    renterInfoReady && polesValid && datesValid &&
    form.agree_terms && form.authorized_rep && signature &&
    stripeReady && confirmPaymentFn && !honeypot &&
    signerMatchesRenter
  );

  const handleSubmit = async () => {
    if (honeypot) return;
    if (!canSubmit) {
      if (!datesValid) toast.error("Please enter valid pickup and return dates");
      else if (!polesValid) toast.error("Please complete every pole row (length + weight)");
      else if (!signerMatchesRenter) toast.error("Signer name must match the Renter (Coach/Parent/Guardian) for a minor athlete.");
      else toast.error("Please complete all required fields, sign, and add payment method");
      return;
    }
    setSubmitting(true);
    try {
      let paymentMethodId = null;
      try {
        paymentMethodId = await confirmPaymentFn();
      } catch (err) {
        toast.error(`Card error: ${err.message}`);
        setSubmitting(false);
        return;
      }

      await axios.post(`${API}/meet-rentals/submit`, {
        athlete_name: form.athlete_name,
        athlete_is_minor: form.athlete_is_minor,
        renter_name: form.athlete_is_minor ? form.renter_name : '',
        phone: form.phone,
        email: form.email,
        pickup_date: form.pickup_date,
        return_date: form.return_date,
        poles: poles.map(p => ({ length: p.length, weight: p.weight })),
        authorized_rep: form.authorized_rep,
        signature_date: form.signature_date,
        signature_data: signature,
        agree_terms: form.agree_terms,
        stripe_customer_id: stripeData.customerId,
        stripe_payment_method_id: paymentMethodId,
        meet_name: form.meet_name,
        notes: form.notes,
      });

      toast.success("Meet rental request submitted!");
      setStep('complete');
    } catch (err) {
      toast.error(errToMessage(err, "Failed to submit"));
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 'complete') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full" data-testid="meet-rental-complete-card">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Request received!</h2>
            <p className="text-slate-600 mb-2">
              Thanks {form.athlete_name}. Your meet rental request has been submitted to Circus Pole Vault.
            </p>
            <p className="text-slate-600 mb-4">
              Your card is on file but <strong>has not been charged</strong>. We&apos;ll bill it when you pick up the poles.
            </p>
            <p className="text-sm text-slate-500">We&apos;ll reach out to confirm pickup. You can close this page.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold uppercase tracking-wide mb-3">
            <Trophy className="w-3.5 h-3.5" /> Meet Rental
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Pole Vault Meet Rental Request</h1>
          <p className="text-slate-500 text-sm mt-1">Renting poles for a single track meet? Fill this out and we&apos;ll be in touch.</p>
        </div>

        {/* Renter Information */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
              <User className="w-4 h-4 text-slate-500" /> Renter Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Athlete&apos;s Name <span className="text-red-500">*</span></Label>
                <Input
                  data-testid="meet-athlete-name-input"
                  value={form.athlete_name}
                  onChange={(e) => setForm(f => ({ ...f, athlete_name: e.target.value }))}
                  placeholder="Person using the poles"
                />
              </div>
              <div>
                <Label className="text-xs">Phone <span className="text-red-500">*</span></Label>
                <Input
                  data-testid="meet-phone-input"
                  value={form.phone}
                  onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="(000) 000-0000"
                />
              </div>
              <div>
                <Label className="text-xs">Email <span className="text-red-500">*</span></Label>
                <Input
                  data-testid="meet-email-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <Label className="text-xs">Meet Name</Label>
                <Input
                  data-testid="meet-name-input"
                  value={form.meet_name}
                  onChange={(e) => setForm(f => ({ ...f, meet_name: e.target.value }))}
                  placeholder="E.g. State Championships 2026"
                />
              </div>
            </div>

            {/* Minor toggle + conditional renter (coach/parent/guardian) */}
            <div className="mt-4 bg-slate-50 rounded-lg p-3 border border-slate-200">
              <div className="flex items-center gap-2">
                <Checkbox
                  data-testid="meet-is-minor-checkbox"
                  id="meet-is-minor"
                  checked={form.athlete_is_minor}
                  onCheckedChange={(v) => setForm(f => ({ ...f, athlete_is_minor: !!v, renter_name: v ? f.renter_name : '' }))}
                />
                <Label htmlFor="meet-is-minor" className="text-sm cursor-pointer">
                  Athlete is a minor (under 18)
                </Label>
              </div>
              {form.athlete_is_minor && (
                <div className="mt-3">
                  <Label className="text-xs">
                    Renter&apos;s Name — Coach / Parent / Guardian <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    data-testid="meet-renter-name-input"
                    value={form.renter_name}
                    onChange={(e) => setForm(f => ({ ...f, renter_name: e.target.value }))}
                    placeholder="Adult responsible for the minor athlete"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Because the athlete is a minor, an adult must be named as the renter.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Dates */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-500" /> Pickup &amp; Return Dates
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Pickup Date <span className="text-red-500">*</span></Label>
                <Input
                  data-testid="meet-pickup-date-input"
                  type="date"
                  value={form.pickup_date}
                  onChange={(e) => setForm(f => ({ ...f, pickup_date: e.target.value }))}
                  min={today}
                />
              </div>
              <div>
                <Label className="text-xs">Return Date <span className="text-red-500">*</span></Label>
                <Input
                  data-testid="meet-return-date-input"
                  type="date"
                  value={form.return_date}
                  onChange={(e) => setForm(f => ({ ...f, return_date: e.target.value }))}
                  min={form.pickup_date || today}
                />
              </div>
            </div>
            {form.pickup_date && form.return_date && form.pickup_date > form.return_date && (
              <p className="text-xs text-red-500 mt-2">Return date must be on or after pickup date.</p>
            )}
          </CardContent>
        </Card>

        {/* Poles */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <FileSignature className="w-4 h-4 text-slate-500" /> Poles Needed <span className="text-red-500">*</span>
              </h3>
              <span className="text-xs text-slate-500">All ESSX</span>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Add one row per pole you need. Need two of the same? Just add it twice.
            </p>
            <div className="space-y-2">
              {poles.map((pole, i) => (
                <div key={i} className="flex gap-2 items-end" data-testid={`meet-pole-row-${i}`}>
                  <div className="flex-1">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Length</Label>
                    <SearchableSelect
                      value={pole.length}
                      onChange={(v) => setPole(i, 'length', v)}
                      options={POLE_LENGTHS}
                      placeholder="e.g. 13'7"
                      testId={`meet-pole-length-${i}`}
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">Weight</Label>
                    <SearchableSelect
                      value={pole.weight}
                      onChange={(v) => setPole(i, 'weight', v)}
                      options={POLE_WEIGHTS}
                      placeholder="e.g. 155"
                      testId={`meet-pole-weight-${i}`}
                    />
                  </div>
                  <Button
                    data-testid={`meet-pole-remove-${i}`}
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-700 mb-1"
                    onClick={() => removePole(i)}
                    disabled={poles.length === 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            <Button
              data-testid="meet-add-pole-btn"
              variant="outline"
              size="sm"
              className="mt-3 gap-2"
              onClick={addPole}
            >
              <Plus className="w-4 h-4" /> Add another pole
            </Button>
          </CardContent>
        </Card>

        {/* Agreement */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
              <FileSignature className="w-4 h-4 text-slate-500" /> Meet Rental Agreement
            </h3>
            <div className="bg-slate-50 rounded-lg p-4 max-h-[280px] overflow-y-auto text-xs text-slate-700 whitespace-pre-line leading-relaxed border">
              {AGREEMENT_TEXT}
            </div>
            <div className="flex items-center gap-2 mt-4">
              <Checkbox
                data-testid="meet-agree-checkbox"
                id="meet-agree"
                checked={form.agree_terms}
                onCheckedChange={(v) => setForm(f => ({ ...f, agree_terms: !!v }))}
              />
              <Label htmlFor="meet-agree" className="text-sm cursor-pointer">
                I agree to the above terms <span className="text-red-500">*</span>
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Signature */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
              <FileSignature className="w-4 h-4 text-slate-500" /> Signature <span className="text-red-500">*</span>
            </h3>
            <div>
              <Label className="text-xs">Authorized Representative / Parent / Guardian <span className="text-red-500">*</span></Label>
              <Input
                data-testid="meet-authorized-rep-input"
                value={form.authorized_rep}
                onChange={(e) => setForm(f => ({ ...f, authorized_rep: e.target.value }))}
                placeholder={form.athlete_is_minor && form.renter_name ? form.renter_name : "Printed name"}
                className={`mb-1 ${form.athlete_is_minor && form.authorized_rep && !signerMatchesRenter ? 'border-red-400' : ''}`}
              />
              {form.athlete_is_minor && (
                <p className={`text-[11px] mb-3 ${form.authorized_rep && !signerMatchesRenter ? 'text-red-600' : 'text-slate-500'}`} data-testid="meet-signer-match-hint">
                  {form.authorized_rep && !signerMatchesRenter
                    ? `Must match the renter's name above ("${form.renter_name}"). Capitalization doesn't matter.`
                    : `Because the athlete is a minor, the signer must be the named renter ("${form.renter_name || '—'}").`}
                </p>
              )}
            </div>
            <SignaturePad onSignatureChange={setSignature} />
            <div className="mt-3">
              <Label className="text-xs">Signature Date</Label>
              <Input
                type="date"
                value={form.signature_date}
                onChange={(e) => setForm(f => ({ ...f, signature_date: e.target.value }))}
                className="w-48"
              />
            </div>
          </CardContent>
        </Card>

        {/* Price Estimate */}
        <Card className="mb-6 border-emerald-200 bg-emerald-50/40" data-testid="meet-price-estimate-card">
          <CardContent className="pt-4">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-emerald-600" /> Estimated Price
            </h3>
            {validPoleCount === 0 ? (
              <p className="text-sm text-slate-500">Add at least one pole to see an estimate.</p>
            ) : (
              <>
                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-3xl font-bold text-emerald-700" data-testid="meet-estimate-total">
                      ${estimate.total.toFixed(2)}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {validPoleCount} pole{validPoleCount === 1 ? '' : 's'} × ${estimate.perPole}/pole
                      <span className="ml-2 text-slate-400">({estimate.tier})</span>
                    </div>
                  </div>
                  <div className="text-right text-xs text-slate-500">
                    <div>Per 4-day rental</div>
                  </div>
                </div>
                <div className="mt-3 p-2.5 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                  <strong>Estimate only.</strong> Your card is saved now but is <strong>not</strong> charged until poles are picked up. Final price may vary based on actual pickup/return dates and condition of returned poles.
                </div>
                <p className="text-[11px] text-slate-500 mt-2">
                  Renting for 30+ days? A monthly subscription rental is usually a better deal — let us know after you submit.
                </p>
                <div className="mt-3 pt-3 border-t border-emerald-200 text-[11px] text-slate-500">
                  <strong className="text-slate-700">Pricing per 4-day meet rental:</strong> 1 pole = $40 · 2–3 poles = $30 each · 4+ poles = $25 each.
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Payment Method */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <h3 className="font-semibold text-sm mb-1 flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-slate-500" /> Payment Method <span className="text-red-500">*</span>
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              We&apos;ll save your card now and only charge it when you pick up the poles.
            </p>
            {!renterInfoReady ? (
              <div className="flex items-center gap-2 py-4 text-slate-500 text-sm">
                <AlertCircle className="w-4 h-4" />
                Complete the renter information above to add a card.
              </div>
            ) : stripeLoading ? (
              <div className="flex items-center gap-2 py-4 text-slate-500 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading payment form...
              </div>
            ) : stripeReady ? (
              <CardForm
                customerId={stripeData.customerId}
                clientSecret={stripeData.clientSecret}
                onPaymentMethodReady={(fn) => setConfirmPaymentFn(() => fn)}
              />
            ) : (
              <div className="text-sm text-red-500">Could not load payment form. Please refresh.</div>
            )}
          </CardContent>
        </Card>

        {/* Honeypot */}
        <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
          <input value={honeypot} onChange={(e) => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off" />
        </div>

        {/* Submit */}
        <Button
          data-testid="meet-submit-btn"
          size="lg"
          className="w-full"
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
        >
          {submitting ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
          ) : (
            <><ChevronRight className="w-4 h-4 mr-2" /> Submit Meet Rental Request</>
          )}
        </Button>
        <p className="text-center text-xs text-slate-400 mt-3">
          Secured by Stripe. Card encrypted and saved for pickup billing only.
        </p>
      </div>
    </div>
  );
}


export default function MeetRentalRequestPage() {
  return (
    <Elements stripe={stripePromise}>
      <MeetRentalFormContent />
    </Elements>
  );
}
